import {isNumber} from "lodash"
import {FC} from "react"
import {Trans} from "react-i18next"
import {useNavigate} from "react-router-dom"
import {ReactSVG} from "react-svg"

import {useTranslation} from "@frontend/i18n"
import {makePath} from "@frontend/routing"
import {activeTheme} from "@frontend/themes/activeTheme"
import type {CaseInfoInput} from "@frontend/utils/appHostFunctions"
import {toReplacementEstimateInput} from "@frontend/utils/appHostFunctions"
import {emitHostPeekSelected} from "@frontend/utils/hostJourneyEvents"
import {trpc} from "@frontend/utils/trpc"
import {useHostReplacementEstimate} from "@frontend/utils/useHostReplacementEstimate"
import {
  ALL_REMEDIATION_TYPES,
  Cost,
  PriceRanges,
  Recommendation,
  type RemediationType,
} from "@ri2/db/client"
import {css, cx} from "@styled-system/css"
import {hstack, iconsColor, vstack} from "@styled-system/patterns"

interface Props {
  caseId: string
  priceRanges: PriceRanges
  recommendation: Recommendation | null
  offeredTypes?: RemediationType[]
  product: CaseInfoInput
  className?: string
}

const PEEK_ENABLED = import.meta.env.VITE_PEEK_ENABLED !== "false"

const {accentGlyph: homeVector} = activeTheme.assets

export const Peek: FC<Props> = ({
  caseId,
  priceRanges,
  recommendation,
  offeredTypes = ALL_REMEDIATION_TYPES,
  product,
  className,
}) => {
  const navigate = useNavigate()
  const t = useTranslation()
  const {estimate: hostEstimate, isLoading: isHostEstimateLoading} =
    useHostReplacementEstimate({
      enabled: PEEK_ENABLED,
      caseId,
      product,
    })
  const estimateInput = toReplacementEstimateInput(hostEstimate)
  const {data: overridden, isFetching: isServerOverrideFetching} =
    trpc.casePageData.useQuery(
      {
        id: caseId,
        replacementEstimate: estimateInput,
      },
      {enabled: PEEK_ENABLED && !!estimateInput},
    )
  const peek = overridden?.peek
  const replaceCost = peek?.priceRanges.replaceCost ?? priceRanges.replaceCost
  const activeRecommendation = peek?.recommendation ?? recommendation
  const activeOfferedTypes = peek?.offeredTypes ?? offeredTypes
  const disclosure = peek?.hostEstimate?.disclosure
  const isReplacePriceLoading =
    isHostEstimateLoading ||
    (!!estimateInput && (isServerOverrideFetching || !overridden))

  if (!PEEK_ENABLED) return null

  const onClick = (): void => {
    emitHostPeekSelected(caseId)
    navigate(
      makePath({
        name: "cases",
        caseId,
        causeOnMessageId: "peek",
      }),
    )
  }

  const priceBoxClassName = css({
    flexBasis: 0,
    flexGrow: 1,
  })

  return (
    <div
      onClick={onClick}
      data-test-id="peek-price-ranges"
      className={cx(
        vstack({
          borderRadius: 12,
          borderWidth: 1,
          borderColor: "border.brand.secondary",
          backgroundColor: "background.primary",
          alignItems: "stretch",
          cursor: "pointer",
        }),
        className,
      )}
    >
      <p
        className={css({
          paddingX: 16,
          paddingTop: 16,
          textStyle: "captionMedium",
        })}
      >
        <Trans t={t} i18nKey="cases.case.priceRanges.title">
          <span className={css({textStyle: "captionRegular"})} />
        </Trans>
      </p>
      <div
        className={hstack({
          paddingX: 12,
          paddingBottom: 12,
          gap: 8,
          alignItems: "stretch",
        })}
      >
        {[
          {
            type: "diyRemediation" as const,
            cost: priceRanges.diyCost,
            label: t("cases.case.priceRanges.types.diy"),
            isLoading: false,
          },
          {
            type: "difmRemediation" as const,
            cost: priceRanges.difmCost,
            label: t("cases.case.priceRanges.types.difm"),
            isLoading: false,
          },
          {
            type: "replaceRemediation" as const,
            cost: replaceCost,
            label: t("cases.case.priceRanges.types.replace"),
            isLoading: isReplacePriceLoading,
          },
        ]
          .filter(({type}) => activeOfferedTypes.includes(type))
          .map(({type, cost, label, isLoading}) => (
            <PriceBox
              key={type}
              cost={cost}
              label={label}
              onClick={onClick}
              isRecommended={activeRecommendation?.type === type}
              isLoading={isLoading || undefined}
              className={priceBoxClassName}
            />
          ))}
      </div>
      {disclosure && (
        <p
          className={css({
            paddingX: 16,
            paddingBottom: 12,
            textStyle: "captionRegular",
            color: "text.quaternary",
          })}
        >
          {disclosure}
        </p>
      )}
    </div>
  )
}

interface PriceBoxProps {
  cost: Cost | null
  label: string
  className?: string
  onClick: () => void
  isRecommended?: boolean
  isLoading?: boolean
}

const PriceBox: FC<PriceBoxProps> = ({
  cost,
  label,
  isRecommended,
  isLoading,
  className,
  onClick,
}) => {
  const t = useTranslation()

  const priceString = cost
    ? cost.range && cost.range.low !== cost.range.high
      ? t("general.priceRangeNoCents", {
          bottom: cost.range.low,
          top: cost.range.high,
        })
      : isNumber(cost.cost)
        ? t("general.priceNoCents", {price: cost.cost})
        : null
    : t("cases.case.priceRanges.notAvailable")

  return (
    <button
      onClick={onClick}
      type="button"
      aria-busy={isLoading || undefined}
      className={cx(
        vstack({
          borderRadius: 8,
          backgroundColor: "background.secondary",
          paddingY: 8,
          justifyContent: "center",
          alignItems: "center",
          gap: 0,
          color: "text.tertiary",
          position: "relative",
          overflow: "visible",
          borderWidth: isRecommended ? 1 : 0,
          borderColor: "border.brand.accent",
        }),
        className,
      )}
    >
      <div
        className={css({
          textStyle: "bodyLight",
          textAlign: "center",
          minHeight: "1.25em",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        })}
      >
        {isLoading ? (
          <span
            aria-hidden="true"
            className={css({
              display: "inline-block",
              width: "56px",
              height: "14px",
              borderRadius: "4px",
              backgroundColor: "background.quaternary",
              opacity: 0.7,
            })}
          />
        ) : (
          priceString
        )}
      </div>
      <div className={css({textStyle: "captionMedium", textAlign: "center"})}>
        {label}
      </div>
      <div
        className={vstack({
          borderRadius: 8,
          position: "absolute",
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "background.subtle.brand",
          textStyle: "captionMedium",
          color: "text.brand.accent",
          opacity: 0,

          _hover: {
            opacity: 1,
          },
        })}
      >
        {t("cases.case.priceRanges.learnMore")}
      </div>
      {isRecommended && (
        <div
          className={css({
            position: "absolute",
            top: "-14px",
            right: "-14px",
            zIndex: "causes.sparkle",
          })}
        >
          <ReactSVG
            src={homeVector}
            className={cx(
              iconsColor({color: "foreground.brand.accent"}),
              css({
                "& svg": {
                  width: "27px",
                  height: "27px",
                },
              }),
            )}
          />
        </div>
      )}
    </button>
  )
}
